import { collectHeaders, joinUrlAndQuery, normalizeMethod, normalizeUrl } from '../../../libs/http.js';
const formatImport = (entry) => {
    if (entry.includes(' ')) {
        return entry;
    }
    return goString(entry);
};
const goString = (value) => JSON.stringify(value);
const toPrettyJson = (text) => {
    if (text === undefined) {
        return '';
    }
    try {
        return JSON.stringify(JSON.parse(text), null, 2);
    }
    catch {
        return text;
    }
};
const canUseRawStringLiteral = (value) => {
    return !value.includes('`');
};
const toGoStringLiteral = (value, preferRawLiteral = false) => {
    if (preferRawLiteral && canUseRawStringLiteral(value)) {
        return `\`${value}\``;
    }
    return goString(value);
};
const buildBodySection = (postData) => {
    const imports = new Set();
    if (!postData) {
        return {
            imports,
            setupLines: [],
            requestBody: 'nil',
            needsMultipartContentTypeHeader: false,
        };
    }
    if (postData.mimeType === 'application/x-www-form-urlencoded' && postData.params?.length) {
        imports.add('neturl "net/url"');
        imports.add('strings');
        return {
            imports,
            setupLines: [
                'postData := neturl.Values{}',
                ...postData.params.map((param) => `postData.Set(${goString(param.name)}, ${goString(param.value ?? '')})`),
            ],
            requestBody: 'strings.NewReader(postData.Encode())',
            needsMultipartContentTypeHeader: false,
        };
    }
    if (postData.mimeType === 'multipart/form-data' && postData.params?.length) {
        imports.add('bytes');
        imports.add('mime/multipart');
        const setupLines = ['payload := &bytes.Buffer{}', 'writer := multipart.NewWriter(payload)'];
        const hasFile = postData.params.some((param) => param.fileName !== undefined);
        let hasDeclaredPart = false;
        let hasDeclaredFile = false;
        if (hasFile) {
            imports.add('os');
            imports.add('io');
        }
        postData.params.forEach((param) => {
            setupLines.push('');
            if (param.fileName !== undefined) {
                setupLines.push(`part, _ ${hasDeclaredPart ? '=' : ':='} writer.CreateFormFile(${goString(param.name)}, ${goString(param.fileName)})`);
                setupLines.push('');
                setupLines.push(`f, _ ${hasDeclaredFile ? '=' : ':='} os.Open(${goString(param.fileName)})`);
                setupLines.push('defer f.Close()');
                setupLines.push('');
                setupLines.push('_, _ = io.Copy(part, f)');
                hasDeclaredPart = true;
                hasDeclaredFile = true;
            }
            else {
                setupLines.push(`_ = writer.WriteField(${goString(param.name)}, ${goString(param.value ?? '')})`);
            }
        });
        setupLines.push('writer.Close()');
        return {
            imports,
            setupLines,
            requestBody: 'payload',
            needsMultipartContentTypeHeader: true,
        };
    }
    imports.add('strings');
    const payload = postData.mimeType === 'application/json' ? toPrettyJson(postData.text) : (postData.text ?? '');
    return {
        imports,
        setupLines: [
            `payload := strings.NewReader(${toGoStringLiteral(payload, postData.mimeType === 'application/json')})`,
        ],
        requestBody: 'payload',
        needsMultipartContentTypeHeader: false,
    };
};
const buildImports = (bodyImports) => {
    return Array.from(new Set(['fmt', 'io', 'net/http', ...bodyImports])).sort();
};
/**
 * go/native
 */
export const goNative = {
    target: 'go',
    client: 'native',
    title: 'NewRequest',
    generate(request, configuration) {
        if (!request) {
            return '';
        }
        const method = normalizeMethod(request.method);
        const fullUrl = normalizeUrl(joinUrlAndQuery(request.url ?? '', request.queryString));
        const headers = collectHeaders(request.headers, request.cookies);
        const bodySection = buildBodySection(request.postData);
        const imports = buildImports(bodySection.imports);
        const lines = [
            'package main',
            '',
            'import (',
            ...imports.map((entry) => `\t${formatImport(entry)}`),
            ')',
            '',
            'func main() {',
            `\trequestUrl := ${goString(fullUrl)}`,
            '',
            ...bodySection.setupLines.map((line) => (line ? `\t${line}` : '')),
        ];
        if (bodySection.setupLines.length) {
            lines.push('');
        }
        lines.push(`\treq, _ := http.NewRequest(${goString(method)}, requestUrl, ${bodySection.requestBody})`);
        lines.push('');
        if (bodySection.needsMultipartContentTypeHeader) {
            lines.push('\treq.Header.Set("Content-Type", writer.FormDataContentType())');
        }
        if (configuration?.auth?.username && configuration?.auth?.password) {
            lines.push(`\treq.SetBasicAuth(${goString(configuration.auth.username)}, ${goString(configuration.auth.password)})`);
        }
        headers.forEach((header) => {
            lines.push(`\treq.Header.Add(${goString(header.name)}, ${goString(header.value)})`);
        });
        if (bodySection.needsMultipartContentTypeHeader ||
            headers.length ||
            (configuration?.auth?.username && configuration.auth.password)) {
            lines.push('');
        }
        lines.push('\tres, _ := http.DefaultClient.Do(req)');
        lines.push('');
        lines.push('\tdefer res.Body.Close()');
        lines.push('\tbody, _ := io.ReadAll(res.Body)');
        lines.push('');
        lines.push('\tfmt.Println(res)');
        lines.push('\tfmt.Println(string(body))');
        lines.push('');
        lines.push('}');
        return lines.join('\n');
    },
};
